import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';

import useCountry from 'hooks/useCountry';
import useFeatureFlags from 'hooks/useFeatureFlags';

import { Grid, Hidden } from '@material-ui/core';
import Typography from 'components/CncUI/Typography';
import Card from 'components/CncUI/Card';
import ContentBox from 'components/CncUI/ContentBox';
import Dropdown from 'components/CncUI/Dropdown';
import Link from 'components/CncUI/Link';
import Tooltip from 'components/CncUI/Tooltip';

import crewSupportTopics from 'constants/componentData/crewSupportTopics';
import supportDetailsSelector from 'constants/componentData/crewSupportOptions';

import MailIcon from '@material-ui/icons/Mail';
import PhoneIcon from '@material-ui/icons/Phone';
import WebIcon from '@material-ui/icons/Language';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SupportIcon from 'images/support-icon-24px.png';
import cncPaystubExample from 'images/paystub-example-Cast&Crew.jpg';
import capsPaystubExample from 'images/paystub-example-CAPS.jpg';

const FLAG_LOANOUTS = 'MyCnC-LoanOuts';

const useStyles = makeStyles(({ palette, spacing, breakpoints }) => ({
  header: {
    marginTop: spacing(5),
    marginBottom: spacing(1),
    [breakpoints.down('sm')]: {
      marginTop: spacing(3),
    },
  },
  formContainer: {
    padding: spacing(3),
    '& h4': {
      color: palette.cncUI.primary.gray['+0'],
    },
  },
  dropdown: {
    marginBottom: spacing(1),
  },
  supportDetails: {
    backgroundColor: palette.cncUI.default.table,
    boxShadow: `0px 1px 1px ${palette.cncUI.default['+8']}`,
    marginTop: -spacing(1),
    padding: spacing(3),
  },
  displayRow: {
    paddingTop: '0 !important',
  },
  detailsHeadline: {
    color: palette.cncUI.default.primary,
    margin: 0,
  },
  icon: {
    color: palette.cncUI.default.primary,
    fontSize: 24,
    marginRight: spacing(1),
  },
  link: {
    [breakpoints.down('xs')]: {
      fontSize: 'small',
    },
  },
  tooltipIcon: {
    margin: `${spacing(3)}px auto`,
    [breakpoints.down('sm')]: {
      margin: '24px 8px 24px -8px',
    },
  },
  tooltip: {
    maxWidth: 410,
  },
  tooltipContent: {
    '& p': {
      color: 'white',
    },
  },
  paystubExample: {
    maxHeight: 133,
    maxWidth: 300,
  },
  cardContent: {
    paddingTop: spacing(2),
    paddingRight: spacing(3),
    paddingLeft: spacing(3),
  },
}));

const CrewSupport = () => {
  const classes = useStyles();
  const { country } = useCountry();
  const isCanada = country === 'CAN';
  const { isEnabled } = useFeatureFlags();
  const allowLoanout = isEnabled(FLAG_LOANOUTS);

  const { control, watch, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      topic: '',
      taxDoc: '',
      year: '',
      source: '',
      region: '',
      product: '',
    },
  });

  const form = watch();
  const renderSupportDetails = formState.isValid;

  const labels = {
    topic: 'What is your question or request related to?',
    taxDoc: 'For which tax document specifically?',
    year: 'For which year?',
    source: 'Where are your paystubs from?',
    region: 'For which region of employment?',
    product: 'For which product?',
  };

  const topics = [
    { label: 'Tax Documents', value: 'Tax Documents' },
    {
      label: 'Profile or Direct Deposit',
      value: 'Profile or Direct Deposit',
    },
    {
      label: 'Pay Stubs',
      value: 'Pay Stubs',
    },
    {
      label: 'W-4 / TD-1 Tax Withholding',
      value: 'W-4 / TD-1 Tax Withholding',
    },
    { label: 'Employment Verification', value: 'Employment Verification' },
    {
      label: 'Product troubleshooting or support',
      value: 'Product troubleshooting or support',
    },
  ];

  const renderQuestion = props => (
    <Dropdown
      id={`CrewSupport-${props.name}`}
      label={labels[props.name]}
      placeholder="Select an option"
      data={
        !allowLoanout && props.name === 'topic'
          ? topics
          : crewSupportTopics[props.name]
      }
      value={props.value}
      onChange={e => props.onChange(e.target.value)}
      inputRef={props.ref}
      fullWidth
      className={classes.dropdown}
      inputProps={{
        id: `CrewSupport-${props.name}-input`,
      }}
    />
  );

  const displayTaxDocInput = form.topic === 'Tax Documents';
  const displayPaystubSourceInput =
    (form.taxDoc === 'W-2' && form.year === 'Tax year before 2018') ||
    (form.taxDoc && form.taxDoc !== 'W-2');
  const displayRegionInput = form.topic === 'Employment Verification';
  const displayProductInput =
    form.topic === 'Product troubleshooting or support';
  const displayCaps = (form.taxDoc || '').match(/W-2|1099/);

  const supportDetails = supportDetailsSelector(form, isCanada);

  const tooltipContent = (
    <Grid
      id="CrewSupport-SourceTooltip-Content"
      container
      spacing={2}
      className={classes.tooltipContent}
    >
      <Grid item xs={12}>
        <Typography>
          Refer to the top-left corner of your paystub or paycheck.
        </Typography>
      </Grid>
      <Grid item xs={12} id="CrewSupport-CncExample">
        <Grid container spacing={2}>
          <Grid item xs={3} container direction="column" justify="center">
            <Typography align="right">
              <strong>Cast & Crew</strong> example
            </Typography>
          </Grid>

          <Grid item xs={9}>
            <img
              src={cncPaystubExample}
              alt="Cast&Crew Paystub example"
              className={classes.paystubExample}
            />
          </Grid>
        </Grid>
      </Grid>
      {displayCaps && (
        <Grid item xs={12} id="CrewSupport-CapsExample">
          <Grid container spacing={2}>
            <Grid item xs={3} container direction="column" justify="center">
              <Typography align="right">
                <strong>CAPS, ETC, or PAI</strong> example
              </Typography>
            </Grid>

            <Grid item xs={9}>
              <img
                src={capsPaystubExample}
                alt="CAPS Paystub example"
                className={classes.paystubExample}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} id="CrewSupport-Header">
        <Typography
          variant="h2"
          className={classes.header}
          id="CrewSupport-Title"
        >
          Support
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item lg={5} md={7} xs={12}>
            <ContentBox id="CrewSupport-Form" className={classes.formContainer}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    Answer a few questions below to identify the appropriate
                    support team to best assist you.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    render={renderQuestion}
                    name="topic"
                    rules={{ required: true }}
                    control={control}
                  />
                </Grid>
                {displayTaxDocInput && (
                  <Grid item xs={12}>
                    <Controller
                      render={renderQuestion}
                      name="taxDoc"
                      rules={{ required: true }}
                      control={control}
                    />
                  </Grid>
                )}
                {form.taxDoc === 'W-2' && (
                  <Grid item xs={12}>
                    <Controller
                      render={renderQuestion}
                      name="year"
                      rules={{ required: true }}
                      control={control}
                    />
                  </Grid>
                )}
                {displayPaystubSourceInput && (
                  <>
                    <Grid item xs={11}>
                      <Controller
                        render={props => (
                          <Dropdown
                            id={`CrewSupport-${props.name}`}
                            label={labels[props.name]}
                            placeholder="Select an option"
                            data={
                              displayCaps
                                ? crewSupportTopics[props.name]
                                : crewSupportTopics.canSource
                            }
                            value={props.value}
                            onChange={e => props.onChange(e.target.value)}
                            inputRef={props.ref}
                            fullWidth
                            className={classes.dropdown}
                            inputProps={{
                              id: `CrewSupport-${props.name}-input`,
                            }}
                          />
                        )}
                        name="source"
                        rules={{ required: true }}
                        control={control}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip
                        id="CrewSupport-SourceTooltip"
                        enterTouchDelay={100}
                        position="right-center"
                        content={tooltipContent}
                        classes={{ tooltip: classes.tooltip }}
                      >
                        <HelpOutlineIcon
                          className={classes.tooltipIcon}
                          id="CrewSupport-SourceTooltip-Icon"
                        />
                      </Tooltip>
                    </Grid>
                  </>
                )}
                {displayRegionInput && (
                  <Grid item xs={12}>
                    <Controller
                      render={renderQuestion}
                      name="region"
                      rules={{ required: true }}
                      control={control}
                    />
                  </Grid>
                )}
                {displayProductInput && (
                  <Grid item xs={12}>
                    <Controller
                      render={renderQuestion}
                      name="product"
                      rules={{ required: true }}
                      control={control}
                    />
                  </Grid>
                )}
                {renderSupportDetails && (
                  <Grid item xs={12}>
                    <ContentBox
                      className={classes.supportDetails}
                      id="CrewSupport-SupportContactDetails"
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography
                            bold
                            variant="body2"
                            className={classes.detailsHeadline}
                            style={{ marginBottom: 8 }}
                          >
                            {supportDetails.header}
                          </Typography>
                        </Grid>
                        {supportDetails.subHeader && (
                          <Grid item xs={12}>
                            <Typography
                              bold
                              className={classes.detailsHeadline}
                            >
                              {supportDetails.subHeader}
                            </Typography>
                          </Grid>
                        )}
                        {supportDetails.email && (
                          <Grid
                            item
                            xs={12}
                            container
                            direction="row"
                            alignItems="center"
                            className={classes.displayRow}
                          >
                            <MailIcon className={classes.icon} />
                            <Link
                              href={`mailto:${supportDetails.email}`}
                              className={classes.link}
                            >
                              {supportDetails.email}
                            </Link>
                          </Grid>
                        )}
                        {supportDetails.phone && (
                          <Grid
                            item
                            xs={12}
                            container
                            direction="row"
                            alignItems="center"
                            className={classes.displayRow}
                          >
                            <PhoneIcon className={classes.icon} />
                            <Link
                              href={`tel:${supportDetails.phone.number ||
                                supportDetails.phone}`}
                              className={classes.link}
                            >
                              {supportDetails.phone.name ||
                                supportDetails.phone}
                            </Link>
                          </Grid>
                        )}
                        {supportDetails.url && (
                          <Grid
                            item
                            xs={12}
                            container
                            direction="row"
                            alignItems="center"
                            className={classes.displayRow}
                          >
                            <WebIcon className={classes.icon} />
                            <Link
                              href={supportDetails.url.address}
                              className={classes.link}
                              target="_blank"
                            >
                              {supportDetails.url.name}
                            </Link>
                          </Grid>
                        )}
                        {supportDetails.subHeader2 && (
                          <Grid item xs={12}>
                            <Typography
                              bold
                              className={classes.detailsHeadline}
                            >
                              {supportDetails.subHeader2}
                            </Typography>
                          </Grid>
                        )}
                        {supportDetails.email2 && (
                          <Grid
                            item
                            xs={12}
                            container
                            direction="row"
                            alignItems="center"
                            className={classes.displayRow}
                          >
                            <MailIcon className={classes.icon} />
                            <Link
                              href={`mailto:${supportDetails.email2}`}
                              className={classes.link}
                            >
                              {supportDetails.email2}
                            </Link>
                          </Grid>
                        )}
                        {supportDetails.phone2 && (
                          <Grid
                            item
                            xs={12}
                            container
                            direction="row"
                            alignItems="center"
                            className={classes.displayRow}
                          >
                            <PhoneIcon className={classes.icon} />
                            <Link
                              href={`tel:${supportDetails.phone2.number ||
                                supportDetails.phone2}`}
                              className={classes.link}
                            >
                              {supportDetails.phone2.name ||
                                supportDetails.phone2}
                            </Link>
                          </Grid>
                        )}
                      </Grid>
                    </ContentBox>
                  </Grid>
                )}
                {!renderSupportDetails && (
                  <Grid item xs={12}>
                    {/* Spacer */}
                  </Grid>
                )}
              </Grid>
            </ContentBox>
          </Grid>
          <Hidden mdDown>
            <Grid item lg={1}>
              {/* Spacer */}
            </Grid>
          </Hidden>
          <Grid item lg={4} md={5} xs={12}>
            <Card id="CrewSupport-HelpCenterCard">
              <Card.Title>Cast & Crew Help Center</Card.Title>
              <Card.Content className={classes.cardContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      For additional help, visit our Help Center which contains
                      articles for a variety of topics regarding MyCast&Crew and
                      our other Plus Suite products.
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    alignItems="center"
                    className={classes.displayRow}
                  >
                    <img
                      src={SupportIcon}
                      alt="support icon"
                      className={classes.icon}
                    />
                    <Link
                      href="https://support.castandcrew.com"
                      id="CrewSupport-SupportCenterLink"
                    >
                      support.castandcrew.com
                    </Link>
                  </Grid>
                </Grid>
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CrewSupport;
